import { useMutation } from '@apollo/client';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils } from '@eluve/apollo-client';
import { CopyRichTextButton, MedicalCode } from '@eluve/blocks';
import {
  Button,
  Caption,
  Heading,
  Icon,
  LoadingIcon,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import {
  amendmentFragment,
  useAmendmentActivity,
  useAppointmentActivity,
  useAppointmentMode,
  useIsAppointmentReadonly,
  useIsUserAppointmentOwner,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { DbEnums, WorkerStatusTypesLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useIsTenantAdminFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  InlineFeedbackBox,
} from '@eluve/smart-blocks';

import { AppointmentBillingCodePrices } from './AppointmentBillingCodePrices';
import { AppointmentBillingCodesAmendmentHistory } from './AppointmentBillingCodesAmendmentHistory';
import { AppointmentBillingCodesEditorContainer } from './AppointmentBillingCodesEditorContainer';
import {
  BillingCodeOption,
  SearchableBillingCodes,
} from './SearchableBillingCodes';
import { useBillingCodeRecommendations } from './useBillingCodeRecommendations';
import { useEditBillingCodes } from './useEditBillingCodes';
import { useSearchCodesForBilling } from './useSearchBillingCodes';
import { billingCodesToMarkdown } from './utils';

export interface AppointmentBillingCodesProps {
  tenantId: string;
  appointmentId: string;
}

const insertBillingCodeAmendmentMutation = graphql(`
  mutation addBillingCodeAmendment($amendmentId: uuid!, $appointmentId: uuid!) {
    insertBillingCodeAmendmentsOne(
      object: { amendmentId: $amendmentId, appointmentId: $appointmentId }
    ) {
      __typename
      id
      tenantId
      appointmentId
      amendmentId
      appointmentId
      createdAt
    }
  }
`);

export const AppointmentBillingCodes: React.FC<
  AppointmentBillingCodesProps
> = ({ tenantId, appointmentId }) => {
  const toast = useToast();
  const { appointmentLlmJobs } = useAppointmentActivity();
  const appointmentMode = useAppointmentMode();
  const { isEmpty: isSummaryEmpty } = useSummary();
  const apiClient = useApiClient();
  const analytics = useAnalytics('amendments');
  const userId = useUserIdFromSession();

  const isReadonly = useIsAppointmentReadonly();
  const isUserApptOwner = useIsUserAppointmentOwner();
  const isTenantAdmin = useIsTenantAdminFromSession();

  const {
    billingCodes,
    recommendations,
    onRemoveBillingCode,
    onAddBillingCode,
    refetchBillingCodes,
  } = useEditBillingCodes({
    tenantId,
    appointmentId,
  });
  const searchCodesForBilling = useSearchCodesForBilling({ tenantId });

  const { onAcceptRecommendation } = useBillingCodeRecommendations({
    appointmentId,
  });

  const llmOutputRecommendationId = recommendations.find(
    (r) => r.recommendationSource === 'LLM Output',
  )?.recommendationSourceId;

  const billingCodesJobStatus = appointmentLlmJobs.find(
    (job) => job.llmOutputType === 'BILLING_CODE_RECOMMENDATION',
  )?.jobStatus;

  const {
    isAmendmentInProgress,
    activeAmendmentId,
    isBillingCodeAmendmentInProgress,
    hasBillingCodeAmendments,
  } = useAmendmentActivity();

  const billingCodesDisabled = isReadonly && !isBillingCodeAmendmentInProgress;

  const [insertBillingCodeAmendment] = useMutation(
    insertBillingCodeAmendmentMutation,
  );

  const isManualChartingMode =
    appointmentMode === DbEnums.AppointmentMode.MANUAL_CHARTING;

  const generateBillingCodes = async () => {
    try {
      const result = await apiClient.appointments.generateBillingCodes({
        params: { tenantId, appointmentId },
        body: {},
      });

      if (result.status !== 200) {
        toast.error(
          'Error generating billing code recommendations. Please try again later.',
        );
        return;
      }
    } catch {
      toast.error(
        'Error generating billing code recommendations. Please try again later.',
      );
    }
  };

  const onAddBillingCodeAmendment = async () => {
    if (!activeAmendmentId) {
      return;
    }

    await insertBillingCodeAmendment({
      variables: {
        amendmentId: activeAmendmentId!,
        appointmentId,
      },
      optimisticResponse: {
        insertBillingCodeAmendmentsOne: {
          __typename: 'BillingCodeAmendments',
          id: activeAmendmentId,
          amendmentId: activeAmendmentId,
          appointmentId,
          createdAt: new Date().toISOString(),
          tenantId,
        },
      },
      update: (_, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: amendmentFragment,
            key: { id: activeAmendmentId },
          },
          (existing) => {
            if (!existing || !data?.insertBillingCodeAmendmentsOne) {
              return null;
            }

            return {
              ...existing,
              billing_code_amendment: {
                __typename: 'BillingCodeAmendments' as const,
                id: data.insertBillingCodeAmendmentsOne.id,
                tenantId,
                amendmentId: activeAmendmentId,
                createdAt: new Date().toISOString(),
                billing_codes: [],
              },
            };
          },
        );
      },
      onError: () => {
        toast.error('Failed to add billing code amendment');
      },
      onCompleted: (data) => {
        if (data.insertBillingCodeAmendmentsOne) {
          analytics.capture('user_inserted_billing_code_amendment', {
            tenantId,
            appointmentId,
            userId: userId,
            amendmentId: activeAmendmentId,
            billingCodeAmendmentId: data.insertBillingCodeAmendmentsOne.id,
          });
        }
      },
    });
  };

  const codeRecommendationsToShow =
    recommendations.filter((c) => !c.isFinal && Boolean(c.medical_code)) ?? [];

  const shouldShowGenerateButton = Boolean(
    isManualChartingMode &&
    !isReadonly &&
    codeRecommendationsToShow.length === 0,
  );

  const isInEditingAmendmentMode =
    isBillingCodeAmendmentInProgress && isUserApptOwner;

  const isInEditingCodesMode = !isReadonly || isInEditingAmendmentMode;

  const shouldShowBillingCodeRecommendations =
    (Boolean(codeRecommendationsToShow.length) || isManualChartingMode) &&
    !isReadonly;

  const canAddAmendment =
    isAmendmentInProgress &&
    !isBillingCodeAmendmentInProgress &&
    isUserApptOwner;

  return (
    <Stack gap={6} className={isInEditingAmendmentMode ? 'mb-80' : ''}>
      <Stack gap={3} justify="between" direction="horizontal">
        <Stack gap={3} wrap direction="horizontal">
          <Heading size="xl" weight="semibold">
            Billing Codes
          </Heading>
          <CopyRichTextButton
            hideContentInTooltip
            markdownContent={billingCodesToMarkdown(billingCodes)}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="-ml-1 p-1">
                <Icon name="Info" className="text-text-primary" />
              </div>
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <span>
                CPT copyright 2025 American Medical Association.
                <br />
                All rights reserved.
              </span>
            </TooltipContent>
          </Tooltip>
        </Stack>
        {canAddAmendment && (
          <Button
            variant="outline"
            size="sm"
            onClick={onAddBillingCodeAmendment}
          >
            Add Amendments
          </Button>
        )}
      </Stack>

      <AppointmentBillingCodesAmendmentHistory
        tenantId={tenantId}
        appointmentId={appointmentId}
      />

      {!isInEditingCodesMode && !hasBillingCodeAmendments && (
        <Stack className="sm:flex-row sm:flex-wrap" gap={3} align="stretch">
          {billingCodes
            .filter((code) => code.medical_code)
            .map((code) => (
              <MedicalCode
                code={code.medical_code!.code}
                codeType={code.medical_code!.codeType}
                description={code.description ?? ''}
                key={code.id}
              />
            ))}

          {billingCodes.length === 0 && (
            <div className="text-text-disabled">No billing codes.</div>
          )}
        </Stack>
      )}

      {isInEditingCodesMode && (
        <AppointmentBillingCodesEditorContainer
          refetchBillingCodes={refetchBillingCodes}
        >
          <SearchableBillingCodes
            searchCodes={searchCodesForBilling}
            disabled={billingCodesDisabled}
            onCodeAdded={(code) => {
              if (!billingCodesDisabled) {
                onAddBillingCode(code);
              }
            }}
            onCodeRemoved={(code) => {
              if (!billingCodesDisabled) {
                onRemoveBillingCode(code);
              }
            }}
            selectedCodes={
              billingCodes
                .map((c) => {
                  const code = c.medical_code;
                  return {
                    ...code,
                    description: c.description ?? code?.description ?? null,
                    userGroupCodeConfigId: null,
                  };
                })
                .filter(Boolean) as BillingCodeOption[]
            }
          />
        </AppointmentBillingCodesEditorContainer>
      )}

      {shouldShowBillingCodeRecommendations && (
        <Stack gap={3}>
          <Caption color="tertiary" className="text-xs">
            Recommended Codes
          </Caption>

          {shouldShowGenerateButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={generateBillingCodes}
              disabled={
                isSummaryEmpty ||
                billingCodesJobStatus === WorkerStatusTypesLookup.IN_PROGRESS
              }
            >
              Generate Recommended Codes
            </Button>
          )}

          {billingCodesJobStatus === WorkerStatusTypesLookup.IN_PROGRESS && (
            <Stack
              className="text-text-tertiary"
              direction="horizontal"
              align="center"
              fullWidth={false}
            >
              <LoadingIcon className="mr-2" />
              <span className="text-sm">Generating recommendations...</span>
            </Stack>
          )}

          {codeRecommendationsToShow.length > 0 && (
            <Stack gap={3} wrap direction="horizontal">
              {codeRecommendationsToShow.map(
                ({
                  id: recommendationId,
                  medical_code,
                  userGroupCodeConfigId,
                }) => {
                  if (!medical_code) {
                    return null;
                  }

                  // TODO(jesse)[ELU-3926]: Better support for multiple matching user group configs
                  const codeConfigs = medical_code.userGroupConfigs ?? [];
                  const codeConfig =
                    codeConfigs.find((c) => c.id === userGroupCodeConfigId) ??
                    codeConfigs[0] ??
                    null;

                  const { code, codeType, description } = medical_code;

                  return (
                    <MedicalCode
                      onClick={() => {
                        if (!billingCodesDisabled) {
                          onAcceptRecommendation({
                            ...medical_code,
                            description: codeConfig?.label ?? description,
                            price: codeConfig?.price ?? null,
                            modifiers:
                              (codeConfig?.modifiers as string[]) ?? null,
                            userGroupCodeConfigId: codeConfig?.id,
                          });
                        }
                      }}
                      startAdornment={
                        <div className="pl-1">
                          <Icon name="Plus" className="text-text-tertiary" />
                        </div>
                      }
                      key={recommendationId}
                      code={code}
                      codeType={codeType}
                      description={codeConfig?.label ?? description ?? ''}
                      type="outlineDashed"
                    />
                  );
                },
              )}
            </Stack>
          )}
        </Stack>
      )}

      <FeatureFlaggedComponent flag="BILLING_CODE_PRICES">
        {!isAmendmentInProgress && (
          <AppointmentBillingCodePrices
            appointmentId={appointmentId}
            isReadonly={!isUserApptOwner && !isTenantAdmin}
            tenantId={tenantId}
          />
        )}
      </FeatureFlaggedComponent>

      {llmOutputRecommendationId && (
        <InlineFeedbackBox
          appointmentId={appointmentId}
          llmOutputId={llmOutputRecommendationId}
          sectionLabel={`Were these billing codes helpful?`}
        />
      )}
    </Stack>
  );
};
